import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiCheckCircle, FiMail, FiPackage } from "react-icons/fi";
import { loadFromStorage } from "../utils/localStorage";
import { formatCurrency } from "../utils/currency";

const OrderSuccessPage = () => {
  const [order, setOrder] = useState(null);
  const [emailPayload, setEmailPayload] = useState(null);
  const [mailtoLink, setMailtoLink] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  useEffect(() => {
    setOrder(loadFromStorage("ep_last_order"));
    setEmailPayload(loadFromStorage("ep_admin_email_payload"));
    setMailtoLink(loadFromStorage("ep_admin_mailto"));
  }, []);

  if (!order) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-center px-4">
        <p className="text-5xl">📦</p>
        <h2 className="text-2xl font-bold">No Order Found</h2>
        <p className="text-gray-500">Place an order first to see the confirmation.</p>
        <Link to="/shop" className="btn-primary">Shop Now</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Success header */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
        className="text-center mb-10"
      >
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <FiCheckCircle size={40} className="text-emerald-600" />
        </div>
        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">Order Confirmed!</h1>
        <p className="text-gray-500 text-lg">
          Thank you, <strong>{order.customer.firstName}</strong>! Your order has been placed successfully.
        </p>
        <div className="inline-flex items-center gap-2 bg-brand-50 border border-brand-200 rounded-full px-5 py-2 mt-4">
          <FiPackage size={16} className="text-brand-600" />
          <span className="font-bold text-brand-700 text-sm">Order #{order.orderId}</span>
        </div>
      </motion.div>

      {/* Order details card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-6 md:p-8 mb-6 space-y-6"
      >
        {/* Order meta */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Order Number</p>
            <p className="font-bold text-gray-900">{order.orderId}</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Order Date</p>
            <p className="font-semibold text-gray-900">{order.orderDateFormatted}</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Status</p>
            <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-xs font-bold px-2.5 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
              {order.status}
            </span>
          </div>
        </div>

        {/* Items */}
        <div>
          <h3 className="font-bold text-gray-900 mb-3">Items Ordered</h3>
          <div className="space-y-3">
            {order.items.map((item) => (
              <div key={item.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                  <p className="text-xs text-gray-500">SKU: {item.sku} · Qty: {item.quantity}</p>
                </div>
                <p className="font-bold text-gray-900">{formatCurrency(item.lineTotal)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div className="border-t border-gray-100 pt-4 space-y-2 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>Shipping</span>
            {order.shippingCost === 0
              ? <span className="text-emerald-600 font-medium">FREE</span>
              : <span>{formatCurrency(order.shippingCost)}</span>
            }
          </div>
          <div className="flex justify-between font-bold text-gray-900 text-lg">
            <span>Total Paid</span>
            <span>{formatCurrency(order.total)}</span>
          </div>
        </div>

        {/* Two columns: shipping + payment */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
          <div>
            <h4 className="font-bold text-gray-900 mb-2 text-sm">Shipping Address</h4>
            <div className="text-sm text-gray-600 space-y-0.5">
              <p>{order.customer.firstName} {order.customer.lastName}</p>
              <p>{order.shippingAddress.address}</p>
              <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zip}</p>
              <p>{order.shippingAddress.country}</p>
              <p className="text-gray-400">{order.customer.email}</p>
            </div>
          </div>
          <div>
            <h4 className="font-bold text-gray-900 mb-2 text-sm">Payment</h4>
            <div className="text-sm text-gray-600 space-y-0.5">
              <p>{order.payment.method}</p>
              <p>Card: {order.payment.maskedCard}</p>
              <p>Expires: {order.payment.expiry}</p>
              <p>Cardholder: {order.payment.cardholderName}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Admin email notification panel */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card p-6 mb-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FiMail size={20} className="text-brand-600" />
            <h3 className="font-bold text-gray-900">Admin Notification</h3>
          </div>
          <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full">
            ✓ Prepared
          </span>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          An order notification has been prepared for the store admin at{" "}
          <strong>admin@storeonline.online</strong> (TS ELITE SOURCE LLC). In a production environment, this would be automatically emailed via your backend.
        </p>

        <div className="flex flex-wrap gap-3">
          {mailtoLink && (
            <a
              href={mailtoLink}
              className="btn-primary text-sm py-2.5 px-5"
            >
              <FiMail size={15} />
              Open in Email Client
            </a>
          )}
          {/* <button
            onClick={() => setShowAdminPanel((p) => !p)}
            className="btn-secondary text-sm py-2.5 px-5"
          >
            {showAdminPanel ? "Hide" : "View"} Email Preview
          </button> */}
        </div>

        {showAdminPanel && emailPayload && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-4"
          >
            <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
              <p className="text-xs text-gray-400 mb-1">To: {emailPayload.to}</p>
              <p className="text-xs text-gray-400 mb-3">Subject: {emailPayload.subject}</p>
              <pre className="text-xs text-green-400 whitespace-pre-wrap font-mono">
                {emailPayload.body}
              </pre>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/shop" className="btn-primary">
          Continue Shopping
        </Link>
        <Link to="/" className="btn-secondary">
          Back to Home
        </Link>
      </div>

      <p className="text-center text-sm text-gray-400 mt-8">
        Questions? Contact us at{" "}
        <a href="mailto:info@e-storeonline.online" className="text-brand-600 hover:underline">
          info@e-storeonline.online
        </a>
      </p>
    </div>
  );
};

export default OrderSuccessPage;
